import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
} from "@floating-ui/react"
import { useNavigate } from "@tanstack/react-router"
import { TrailIcon } from "@ui/components/primitives/icons/TrailIcon"
import { FolderGit2, MessageCircleMore, Puzzle, Search, SquareTerminal } from "lucide-react"
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from "react"

import type {
  SiteNavIcon,
  SiteNavLink as SiteNavLinkItem,
  SiteNavPromo,
} from "@/components/nav-data"
import { ChevronDownIcon } from "@/components/primitives/icons/ChevronDownIcon"

import type { SiteHeaderVariant } from "./siteHeaderVariant"

interface SiteNavMenuProps {
  label: string
  items: SiteNavLinkItem[]
  menuLabel?: string
  promo?: SiteNavPromo
  secondaryItems?: SiteNavLinkItem[]
  variant: SiteHeaderVariant
}

export const menuClassNames = {
  trigger: {
    standard: "text-default hover:bg-hover",
    hero: "text-white/80 hover:bg-white/10 hover:text-white",
  },
  chevron: {
    standard: "text-default",
    hero: "text-white",
  },
  list: {
    standard: "bg-floating text-default",
    hero: "bg-floating text-default",
  },
  item: {
    standard:
      "text-default hover:bg-hover focus:bg-transparent! focus-visible:bg-hover! active:bg-active",
    hero: "text-default hover:bg-hover focus:bg-transparent! focus-visible:bg-hover! active:bg-active",
  },
} as const satisfies Record<
  "trigger" | "chevron" | "list" | "item",
  Record<SiteHeaderVariant, string>
>

const triggerClassName =
  "text-site-body-sm flex h-8 cursor-pointer items-center gap-1 rounded-lg px-2 py-1 font-sans font-normal tracking-[-0.01em] transition-colors focus:outline-2 focus:-outline-offset-2 focus:outline-transparent focus:transition-none focus-visible:outline-focus"

const defaultListClassName =
  "w-54 min-w-54 max-w-54 overflow-hidden rounded-lg [&>.floating-popover-frame]:rounded-lg [&>.floating-popover-frame]:before:rounded-[7px]"

const megaListClassName =
  "w-[641px] min-w-[641px] max-w-[641px] overflow-hidden rounded-lg bg-floating shadow-[0_0_0_1px_rgb(0_0_0/0.1),0_4px_12px_-2px_rgb(0_0_0/0.1)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1),0_4px_12px_-2px_rgb(0_0_0/0.45)] [&>.floating-popover-frame]:rounded-lg [&>.floating-popover-frame]:bg-transparent [&>.floating-popover-frame]:p-0 [&>.floating-popover-frame]:before:hidden"

const defaultListInnerClassName = "flex flex-col gap-1 rounded-[7px] p-2"
const megaListInnerClassName = "flex overflow-hidden rounded-lg p-0"

// Shared with the mobile menu (SiteMobileMenu), which lays out the same items.
export const siteNavLinkClassName =
  "flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-default list-hover-transition hover:bg-hover focus:bg-hover focus:outline-none"

const itemClassName = "h-8! rounded-sm! py-0! font-sans font-medium tracking-[-0.01em]"
const megaItemClassName = "h-16! gap-3! rounded-lg! p-3! font-sans tracking-[-0.01em]"
const promoItemClassName =
  "h-auto! flex-col! items-start! gap-2.5! rounded-sm! p-3! font-sans tracking-[-0.01em]"

const menuIcons = {
  trails: TrailIcon,
  "agentic-search": Search,
  cli: SquareTerminal,
  "git-hosting": FolderGit2,
  "session-storage": MessageCircleMore,
  plugins: Puzzle,
} as const satisfies Record<SiteNavIcon, ComponentType<{ size?: number; className?: string }>>

const warmedPromoImages = new Set<string>()

/**
 * The menu mounts its promo image only when it opens, so without this the
 * thumbnail starts downloading then and pops in late. Fetching it ahead puts it
 * in the browser cache. Every browser the site supports picks the WebP source.
 */
function warmPromoImage(promo: SiteNavPromo) {
  const src = promo.imageWebpSrc ?? promo.imageSrc
  if (warmedPromoImages.has(src)) return
  warmedPromoImages.add(src)
  new Image().src = src
}

function handleInternalNavigation(
  event: MouseEvent<HTMLAnchorElement>,
  href: string,
  onNavigate: (href: string) => void,
) {
  if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
    event.preventDefault()
    onNavigate(href)
  }
}

interface SiteNavMenuContextValue {
  activeIndex: number | null
  getItemProps: ReturnType<typeof useInteractions>["getItemProps"]
  close: () => void
}

const SiteNavMenuContext = createContext<SiteNavMenuContextValue | null>(null)

function SiteNavLink({
  children,
  className,
  isMegaItem,
  item,
  onNavigate,
}: {
  children: ReactNode
  className: string
  isMegaItem?: boolean
  item: SiteNavLinkItem
  onNavigate: (href: string) => void
}) {
  const menu = useContext(SiteNavMenuContext)
  const { ref, index } = useListItem()
  if (!menu) throw new Error("SiteNavLink must be used within SiteNavMenu")

  return (
    <a
      ref={ref}
      href={item.href}
      role="menuitem"
      tabIndex={menu.activeIndex === index ? 0 : -1}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noopener noreferrer" : undefined}
      className={`${siteNavLinkClassName} ${className}`}
      data-site-nav-mega-item={isMegaItem || undefined}
      {...menu.getItemProps({
        onClick(event: MouseEvent<HTMLAnchorElement>) {
          if (!item.external) handleInternalNavigation(event, item.href, onNavigate)
          menu.close()
        },
      })}
    >
      {children}
    </a>
  )
}

function SiteNavMenuItem({
  item,
  variant,
  onNavigate,
}: {
  item: SiteNavLinkItem
  variant: SiteHeaderVariant
  onNavigate: (href: string) => void
}) {
  return (
    <SiteNavLink
      item={item}
      onNavigate={onNavigate}
      className={`${itemClassName} px-3! ${menuClassNames.item[variant]}`}
    >
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
    </SiteNavLink>
  )
}

/** Icon tile, title, badge and description of a mega menu item. */
export function SiteNavMegaItemContent({
  item,
  iconTileClassName = "size-10",
}: {
  item: SiteNavLinkItem
  /** The mobile menu uses a smaller tile. */
  iconTileClassName?: string
}) {
  const Icon = item.icon ? menuIcons[item.icon] : undefined

  return (
    <>
      <span
        aria-hidden="true"
        data-site-nav-icon={item.icon}
        className={`flex shrink-0 items-center justify-center rounded-sm bg-black/5 dark:bg-white/5 ${iconTileClassName}`}
      >
        {Icon && <Icon size={16} className="size-4 text-muted" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm leading-5 font-medium" data-site-nav-item-title>
            {item.label}
          </span>
          {item.badge && (
            <span
              data-site-nav-badge
              className={`flex h-[18px] shrink-0 items-center rounded-sm px-1 text-xs leading-5 font-medium ${
                item.badge === "New" || item.badge === "Updated"
                  ? "bg-accent text-white"
                  : "bg-black/8 text-default dark:bg-white/8"
              }`}
            >
              {item.badge}
            </span>
          )}
        </span>
        {item.description && (
          <span className="truncate text-sm leading-5 font-normal text-muted">
            {item.description}
          </span>
        )}
      </span>
    </>
  )
}

function SiteNavMegaItem({
  item,
  variant,
  onNavigate,
}: {
  item: SiteNavLinkItem
  variant: SiteHeaderVariant
  onNavigate: (href: string) => void
}) {
  return (
    <SiteNavLink
      item={item}
      onNavigate={onNavigate}
      className={`${megaItemClassName} ${menuClassNames.item[variant]}`}
      isMegaItem
    >
      <SiteNavMegaItemContent item={item} />
    </SiteNavLink>
  )
}

export function SiteNavMenuTitle({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-[30px] shrink-0 items-center px-3 pt-1.5 text-[13px] leading-5 text-(--neutral-600)">
      {children}
    </div>
  )
}

function SiteNavMegaMenu({
  items,
  menuLabel,
  promo,
  secondaryItems = [],
  variant,
  onNavigate,
}: {
  items: SiteNavLinkItem[]
  menuLabel: string
  promo: SiteNavPromo
  secondaryItems?: SiteNavLinkItem[]
  variant: SiteHeaderVariant
  onNavigate: (href: string) => void
}) {
  return (
    <>
      <div className="flex w-[400px] shrink-0 flex-col p-2" data-site-nav-mega-primary>
        <SiteNavMenuTitle>{menuLabel}</SiteNavMenuTitle>
        {items.map((item) => (
          <SiteNavMegaItem key={item.href} item={item} variant={variant} onNavigate={onNavigate} />
        ))}
      </div>
      <div aria-hidden="true" className="w-px shrink-0 self-stretch bg-black/10 dark:bg-white/10" />
      <div className="flex w-[240px] shrink-0 flex-col p-2" data-site-nav-mega-secondary>
        <div className="flex shrink-0 items-center px-3 pt-2 pr-1.5 pb-1.5 text-[13px] leading-5 text-(--neutral-600)">
          {promo.label}
        </div>
        <SiteNavLink
          item={{ href: promo.href, label: promo.title }}
          onNavigate={onNavigate}
          className={`${promoItemClassName} ${menuClassNames.item[variant]}`}
        >
          <span className="h-[113px] w-[200px] shrink-0 overflow-hidden rounded-sm">
            <picture>
              {promo.imageWebpSrc ? <source srcSet={promo.imageWebpSrc} type="image/webp" /> : null}
              <img src={promo.imageSrc} alt={promo.imageAlt} className="size-full object-cover" />
            </picture>
          </span>
          <span className="flex flex-col items-start text-sm leading-5">
            <span className="font-medium whitespace-pre-line" data-site-nav-promo-title>
              {promo.title}
            </span>
            <span className="font-normal text-muted">{promo.cta}</span>
          </span>
        </SiteNavLink>
        {secondaryItems.length > 0 && (
          <div
            aria-hidden="true"
            className="-mx-2 my-1.5 h-px shrink-0 bg-black/10 dark:bg-white/10"
          />
        )}
        {secondaryItems.map((item) => (
          <SiteNavMenuItem key={item.href} item={item} variant={variant} onNavigate={onNavigate} />
        ))}
      </div>
    </>
  )
}

function SiteNavMenuList({
  items,
  menuLabel,
  promo,
  secondaryItems,
  variant,
}: {
  items: SiteNavLinkItem[]
  menuLabel?: string
  promo?: SiteNavPromo
  secondaryItems?: SiteNavLinkItem[]
  variant: SiteHeaderVariant
}) {
  const navigate = useNavigate()
  const onNavigate = (href: string) => navigate({ to: href })
  const isMega = Boolean(menuLabel && promo)

  return (
    <div className="floating-popover-frame rounded-lg">
      <div className={isMega ? megaListInnerClassName : defaultListInnerClassName}>
        {isMega ? (
          <SiteNavMegaMenu
            items={items}
            menuLabel={menuLabel!}
            promo={promo!}
            secondaryItems={secondaryItems}
            variant={variant}
            onNavigate={onNavigate}
          />
        ) : (
          items.map((item) => (
            <SiteNavMenuItem
              key={item.href}
              item={item}
              variant={variant}
              onNavigate={onNavigate}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Marketing-only navigation: delayed hover, keyboard activation, and
 * viewport-centered mega menus. Floating UI handles keyboard focus and dismissal
 * without adding website-specific policies to the app's Dropdown primitive.
 */
export function SiteNavMenu({
  label,
  items,
  menuLabel,
  promo,
  secondaryItems,
  variant,
}: SiteNavMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const elementsRef = useRef<(HTMLElement | null)[]>([])
  const isMega = Boolean(menuLabel && promo)
  const {
    refs: { domReference, setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      isMega && {
        name: "centerMegaMenu",
        fn({ rects }) {
          return { x: Math.max(8, (window.innerWidth - rects.floating.width) / 2) }
        },
      },
    ],
  })
  const hover = useHover(context, {
    delay: { open: 200 },
    handleClose: safePolygon(),
  })
  const click = useClick(context, { event: "mousedown", toggle: false, ignoreMouse: true })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: "menu" })
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnOpen: "auto",
    focusItemOnHover: false,
    loop: true,
  })
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
    listNavigation,
  ])

  // Wait for the page's own load so the thumbnail doesn't compete with what the
  // visitor landed on. Skip it while the desktop nav is hidden: the mobile menu
  // has no promo.
  useEffect(() => {
    if (!promo) return
    const warm = () => {
      const trigger = domReference.current
      if (trigger instanceof HTMLElement && trigger.offsetParent) warmPromoImage(promo)
    }
    if (document.readyState === "complete") {
      warm()
      return
    }
    window.addEventListener("load", warm, { once: true })
    return () => window.removeEventListener("load", warm)
  }, [promo, domReference])

  return (
    <>
      <button
        ref={setReference}
        type="button"
        className={`${triggerClassName} ${menuClassNames.trigger[variant]}`}
        data-state={isOpen ? "open" : "closed"}
        {...getReferenceProps({
          onMouseDown: (event) => event.preventDefault(),
          // On a slow load the visitor can reach the menu first.
          onPointerEnter: () => promo && warmPromoImage(promo),
          onFocus: () => promo && warmPromoImage(promo),
        })}
      >
        {label}
        <ChevronDownIcon size={16} className={`${menuClassNames.chevron[variant]} opacity-40`} />
      </button>
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
            <div
              ref={setFloating}
              style={floatingStyles}
              className={`site-nav-popup z-popover ${isMega ? megaListClassName : defaultListClassName} ${menuClassNames.list[variant]}`}
              {...getFloatingProps()}
            >
              <SiteNavMenuContext.Provider
                value={{ activeIndex, getItemProps, close: () => setIsOpen(false) }}
              >
                <FloatingList elementsRef={elementsRef}>
                  <SiteNavMenuList
                    items={items}
                    menuLabel={menuLabel}
                    promo={promo}
                    secondaryItems={secondaryItems}
                    variant={variant}
                  />
                </FloatingList>
              </SiteNavMenuContext.Provider>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}
